// ─── Timeless Elegance V1 ─────────────────────────────────────
// Premium wedding invitation. Ivory-gold palette, soft radius,
// warm overlay, serif headings. Desktop-friendly gallery grid.

export const timelessEleganceV1 = {
  templateId: 'timeless-elegance-v1',
  name: 'Timeless Elegance',
  description: 'Warm ivory tones with gold accents. Classic romantic feel.',
  category: 'wedding',
  previewColors: ['#F8F4EC', '#C8A96B', '#1F1A17'],
  theme: {
    palette: 'ivory-gold',
    fontPair: 'playfair-inter',
    radius: 'soft',
    shadow: 'soft',
    overlay: 'warm-soft',
  },
  settings: {
    musicEnabled: false,
    countdownEnabled: true,
    rsvpEnabled: true,
    showMapLink: true,
  },
  sections: [
    {
      id: 'hero-1',
      type: 'hero',
      variant: 'hero-centered-image',
      visible: true,
      animation: {
        title: 'stagger-text',
        subtitle: 'fade-up',
        background: 'parallax-soft',
        image: 'zoom-soft',
      },
      editableRules: {
        fields: ['introLabel', 'title', 'subtitle', 'dateText', 'backgroundImage', 'ctaText'],
        locked: ['layout', 'spacing', 'typographyScale'],
      },
      data: {
        introLabel: 'Save the Date',
        title: 'Sofia & Miguel',
        subtitle: 'Join us for our wedding celebration',
        dateText: '12 September 2026',
        backgroundImage: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?q=80&w=1600&auto=format&fit=crop',
        ctaText: 'View Details',
      },
    },
    {
      id: 'quote-1',
      type: 'quote',
      variant: 'quote-centered',
      visible: true,
      animation: { section: 'fade-in' },
      editableRules: {
        fields: ['quoteText', 'quoteAuthor'],
        locked: ['layout', 'spacing'],
      },
      data: {
        quoteText: 'A lifetime of love begins with one beautiful day.',
        quoteAuthor: '',
      },
    },
    {
      id: 'story-1',
      type: 'story',
      variant: 'story-image-left',
      visible: true,
      animation: {
        text: 'fade-up',
        image: 'slide-left',
      },
      editableRules: {
        fields: ['title', 'paragraph', 'secondaryParagraph', 'image'],
        locked: ['grid', 'spacing', 'imageRatio'],
      },
      data: {
        title: 'Our Story',
        paragraph: 'We met, we laughed, we grew, and now we are excited to celebrate this next chapter with the people we love most.',
        secondaryParagraph: 'Your presence will make our day even more meaningful, and we cannot wait to share this unforgettable moment with you.',
        image: 'https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=1200&auto=format&fit=crop',
      },
    },
    {
      id: 'details-1',
      type: 'event-details',
      variant: 'details-grid',
      visible: true,
      animation: { items: 'stagger-cards' },
      editableRules: {
        fields: ['date', 'time', 'venue', 'address', 'note'],
        locked: ['grid', 'iconStyle', 'spacing'],
      },
      data: {
        date: 'Saturday, 12 September 2026',
        time: '16:00',
        venue: 'Quinta da Serra',
        address: 'Sintra, Portugal',
        note: 'Reception to follow after the ceremony.',
      },
    },
    {
      id: 'countdown-1',
      type: 'countdown',
      variant: 'countdown-centered',
      visible: true,
      animation: { section: 'fade-in' },
      editableRules: {
        fields: ['targetDateTime', 'label'],
        locked: ['timerLogic', 'layout', 'format'],
      },
      data: {
        targetDateTime: '2026-09-12T16:00:00+01:00',
        label: 'Counting down to our special day',
      },
    },
    {
      id: 'gallery-1',
      type: 'gallery',
      variant: 'gallery-grid',
      visible: true,
      animation: { items: 'zoom-soft' },
      editableRules: {
        fields: ['images'],
        locked: ['grid', 'gap', 'imageRatio'],
      },
      data: {
        images: [
          'https://images.unsplash.com/photo-1522673607200-164d1b6ce486?q=80&w=900&auto=format&fit=crop',
          'https://images.unsplash.com/photo-1519225421980-715cb0215aed?q=80&w=900&auto=format&fit=crop',
          'https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?q=80&w=900&auto=format&fit=crop',
          'https://images.unsplash.com/photo-1520854221256-17451cc331bf?q=80&w=900&auto=format&fit=crop',
        ],
      },
    },
    {
      id: 'venue-1',
      type: 'venue',
      variant: 'venue-split',
      visible: true,
      animation: { section: 'fade-up' },
      editableRules: {
        fields: ['venueName', 'address', 'mapLink', 'note', 'image'],
        locked: ['layout', 'spacing', 'buttonStyle'],
      },
      data: {
        venueName: 'Quinta da Serra',
        address: 'Sintra, Portugal',
        mapLink: 'https://maps.google.com',
        note: 'Parking is available on site.',
        image: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?q=80&w=1200&auto=format&fit=crop',
      },
    },
    {
      id: 'rsvp-1',
      type: 'rsvp',
      variant: 'rsvp-card',
      visible: true,
      animation: { section: 'fade-up' },
      editableRules: {
        fields: ['title', 'description', 'deadline', 'submitLabel'],
        locked: ['validationRules', 'inputStyle', 'spacing'],
      },
      data: {
        title: 'RSVP',
        description: 'Please let us know if you\'ll be joining us.',
        deadline: '20 August 2026',
        submitLabel: 'Send RSVP',
      },
    },
    {
      id: 'footer-1',
      type: 'footer',
      variant: 'footer-centered',
      visible: true,
      animation: { section: 'fade-up' },
      editableRules: {
        fields: ['closingMessage', 'names', 'contactText'],
        locked: ['layout', 'spacing', 'typographyScale'],
      },
      data: {
        closingMessage: 'We can\'t wait to celebrate with you.',
        names: 'Sofia & Miguel',
        contactText: 'For questions, contact us anytime.',
      },
    },
  ],
};
